import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowRightLeft, ChevronLeft, ChevronRight, Layers, X, MoreVertical, ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";
import { formatKpi } from "./presentation";
import GlossaryInfo from "@/components/common/GlossaryInfo";

/**
 * iter-119ae — KPI tile, redesigned per user feedback (the original
 * dashboard UI was described as "clumsy and messy" — 4 hover icons
 * crammed into the header, KPI value not visibly clickable, no drill
 * affordance).
 *
 * Changes from prior:
 *   • All chrome (move ← →, resize, swap, remove) collapsed under a
 *     single `⋮` overflow-menu that opens on click — keeps the tile
 *     visually quiet by default.
 *   • Visible "DRILL ↗" pill above the value tells users the tile is
 *     clickable for drill-down. Renders only when `onDrillDown` is
 *     provided (e.g. real KPIs, not empty slots).
 *   • Numbers now sit on a calmer panel (subtle border, no
 *     `bg-secondary/30` competition with the dashboard background).
 *   • Delta arrow icons replaced the lonely "+12%" text for fast
 *     visual parsing.
 */
export default function KpiTile({
    kpi, slotIndex, isFirst, isLast,
    liveValue, size, rearranging, isSwapOpen,
    pool, slotOverride,
    onMove, onResize, onSwapToggle, onSwap, onSwapReset, onHide, onDrillDown,
    livePriorValue, // iter-121.106 — explicit prior-period value for comparison KPIs
    readOnly,
    onRemoveFromPrebuilt,
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    if (!kpi) {
        return (
            <div className="border border-dashed border-border/60 rounded-md p-3 min-h-[92px] flex items-center justify-center text-[10px] text-muted-foreground"
                 data-testid={`prebuilt-kpi-slot-${slotIndex}`}>
                SLOT {slotIndex + 1}
            </div>
        );
    }
    const sizeClass = size === "sm"
        ? "col-span-1"
        : size === "lg" ? "col-span-2 sm:col-span-4" : "col-span-1 sm:col-span-1";
    const hasData = liveValue != null || kpi.value != null;
    const canDrill = !!onDrillDown && !rearranging && hasData;
    return (
        <div key={kpi.id}
             className={`dash-card p-3 min-h-[92px] relative group hover:border-primary/60 hover:shadow-sm transition-all flex flex-col justify-between ${sizeClass}`}
             data-testid={`prebuilt-kpi-slot-${slotIndex}`}
             data-kpi-id={kpi.id}>
            <div className={`h-full flex flex-col justify-between ${rearranging ? "pointer-events-none select-none" : ""}`}>
                {/* Header row: title + overflow menu */}
                <div className="flex items-start justify-between gap-1 mb-1">
                    <div className="flex items-center gap-1 min-w-0 pr-2">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground truncate" title={kpi.title}>
                            {kpi.title}
                        </span>
                        <GlossaryInfo term={kpi.title} termKey={kpi.key || kpi.metric_key || kpi.id} />
                    </div>
                    {!readOnly && (
                        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                    title="Tile actions"
                                    data-testid={`prebuilt-kpi-menu-${slotIndex}`}
                                >
                                    <MoreVertical className="h-3.5 w-3.5" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-48 p-1" align="end">
                                <button onClick={() => { onMove(kpi.id, -1); setMenuOpen(false); }} disabled={isFirst}
                                        className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                                        data-testid={`prebuilt-kpi-move-left-${slotIndex}`}>
                                    <ChevronLeft className="h-3 w-3" /> Move left
                                </button>
                                <button onClick={() => { onMove(kpi.id, +1); setMenuOpen(false); }} disabled={isLast}
                                        className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                                        data-testid={`prebuilt-kpi-move-right-${slotIndex}`}>
                                    <ChevronRight className="h-3 w-3" /> Move right
                                </button>
                                <button onClick={() => { onSwapToggle(slotIndex); setMenuOpen(false); }}
                                        className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-secondary flex items-center gap-2"
                                        data-testid={`prebuilt-kpi-swap-btn-${slotIndex}`}>
                                    <ArrowRightLeft className="h-3 w-3" /> Swap with pool
                                </button>

                                {onHide && (
                                    <button onClick={() => { onHide(kpi.id); setMenuOpen(false); }}
                                            className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-destructive/10 hover:text-destructive flex items-center gap-2"
                                            data-testid={`prebuilt-kpi-remove-${slotIndex}`}>
                                        <X className="h-3 w-3" /> Remove from strip
                                    </button>
                                )}

                                {onRemoveFromPrebuilt && (
                                    <button onClick={() => { onRemoveFromPrebuilt(kpi.id); setMenuOpen(false); }}
                                            className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-destructive/10 hover:text-destructive flex items-center gap-2"
                                            data-testid={`prebuilt-kpi-remove-prebuilt-${slotIndex}`}>
                                        <X className="h-3 w-3" /> Remove from prebuilt
                                    </button>
                                )}
                            </PopoverContent>
                        </Popover>
                    )}
                </div>

                {/* Value row — clickable when drill-down enabled */}
                <div
                    className={`text-2xl font-semibold truncate font-mono tabular-nums leading-tight ${canDrill ? "cursor-pointer hover:text-primary transition-colors" : ""}`}
                    title={canDrill ? `Click to drill down — ${String(liveValue ?? kpi.value ?? "—")}` : String(liveValue ?? kpi.value ?? "—")}
                    onClick={() => canDrill && onDrillDown(kpi)}
                    data-testid={`prebuilt-kpi-value-${slotIndex}`}
                >
                    {(() => {
                        const formatted = liveValue != null
                            ? formatKpi(liveValue, kpi.format)
                            : (kpi.value != null ? String(kpi.value) : "—");
                        const unit = kpi.unit && !formatted.endsWith(kpi.unit) ? kpi.unit : "";
                        return formatted + unit;
                    })()}
                </div>

                {/* Delta + drill pill row */}
                <div className="flex items-center justify-between gap-2 mt-1.5 min-h-[18px]">
                    {typeof kpi.delta === "number" ? (
                        <div className={`inline-flex items-center gap-0.5 text-[11px] font-mono tabular-nums ${kpi.delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}
                             data-testid={`prebuilt-kpi-delta-${slotIndex}`}>
                            {kpi.delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                            {kpi.delta >= 0 ? "+" : ""}{kpi.delta}%
                        </div>
                    ) : <span />}
                    {canDrill && (
                        <button
                            onClick={() => onDrillDown(kpi)}
                            className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                            title="Open drill-down"
                            data-pdf-exclude="true"
                            data-testid={`prebuilt-kpi-drill-${slotIndex}`}
                        >
                            Drill <ArrowUpRight className="h-2.5 w-2.5" />
                        </button>
                    )}
                </div>

                {/* iter-121.106 — Comparison KPI footer: surface the prior-period
                    context the user asked for (current vs previous year etc.).
                    Honours either:
                      • `kpi.comparison` (set by the AI Builder's add_widget tool)
                      • a non-null `livePriorValue` from the resolve endpoint
                    Without these, the row is omitted (no clutter on plain KPIs). */}
                {(kpi.comparison || livePriorValue != null) && (
                    <div className="mt-1 pt-1 border-t border-border/40 text-[10px] font-mono flex items-center gap-1.5 text-muted-foreground" data-testid={`prebuilt-kpi-comparison-${slotIndex}`}>
                        <span className="uppercase tracking-wider opacity-70">
                            {(kpi.comparison && kpi.comparison.prior_label)
                              || (kpi.comparison && kpi.comparison.window ? `vs prior ${kpi.comparison.window}` : "vs prior")}
                        </span>
                        <span className="tabular-nums">
                            {livePriorValue != null
                                ? formatKpi(livePriorValue, kpi.format)
                                : "—"}
                        </span>
                    </div>
                )}

                {kpi._overridden && (
                    <span className="absolute top-1 left-1 text-[8px] font-mono text-primary/70" title="Personal override">◆</span>
                )}
            </div>
            {isSwapOpen && (
                <div className="absolute top-full left-0 right-0 z-20 mt-1 border border-border rounded-sm bg-popover shadow-lg p-2 max-h-48 overflow-y-auto"
                     data-testid={`prebuilt-swap-menu-${slotIndex}`}>
                    <div className="flex items-center justify-between mb-1">
                        <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">Swap with pool</div>
                        <button onClick={() => onSwapToggle(null)} className="text-muted-foreground hover:text-foreground">
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                    <div className="space-y-1">
                        {pool.length === 0 && (
                            <div className="text-[11px] text-muted-foreground py-2">No pool candidates.</div>
                        )}
                        {pool.map((c) => (
                            <button key={c.id} onClick={() => onSwap(slotIndex, c.id)}
                                    className="w-full text-left text-xs px-2 py-1 rounded-sm hover:bg-primary/10"
                                    data-testid={`prebuilt-swap-candidate-${slotIndex}-${c.id}`}>
                                {c.title}
                            </button>
                        ))}
                        {slotOverride && (
                            <button onClick={() => onSwapReset(slotIndex)}
                                    className="w-full text-left text-[11px] px-2 py-1 rounded-sm hover:bg-secondary text-muted-foreground"
                                    data-testid={`prebuilt-swap-reset-${slotIndex}`}>
                                ← Reset to default
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}